import type { ChangeEvent } from 'react';
import { useMemo, useState } from 'react';

import { useField, useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { API_TOKEN_NAME_MAX_LENGTH } from '@/config/name';
import { DateTextVariants, useDateText } from '@/features/_shared/user';
import { createNameSchema } from '@/features/dashboard/lib/validateName';
import type { ApiKeyOutput } from '@/features/profile/TokenGeneration/api';
import { apiKeysApi, apiKeysOptions } from '@/features/profile/TokenGeneration/api';
import { triggerBlobDownload } from '@/shared/lib/download/triggerBlobDownload';

import { clock } from '@/services/clock';

const useGenerationBody = () => {
  const { formatDateString } = useDateText();
  const [defaultApiKeyName] = useState(
    () => `my_apikey_${formatDateString(clock.date().toJSON(), DateTextVariants.FULL)}`
  );
  const [generated, setGenerated] = useState(false);
  const [newApiKey, setNewApiKey] = useState<ApiKeyOutput | null>(null);
  const queryClient = useQueryClient();

  const existingNames = useMemo(() => {
    const keys = queryClient.getQueryData<ApiKeyOutput[]>(apiKeysOptions.all()) ?? [];
    return keys.map(key => key.label.toLowerCase());
  }, [queryClient]);

  const tokenNameSchema = useMemo(
    () =>
      z.object({
        name: createNameSchema('Token name', API_TOKEN_NAME_MAX_LENGTH, existingNames)
      }),
    [existingNames]
  );

  const {
    mutate,
    isPending: loading,
    isError
  } = useMutation({
    mutationFn: (label: string): Promise<ApiKeyOutput> => apiKeysApi.create(label),
    onSuccess: key => {
      queryClient.setQueryData<ApiKeyOutput[]>(apiKeysOptions.all(), old =>
        old ? [...old, key] : [key]
      );
      setNewApiKey(key);
      setGenerated(true);
    }
  });

  const form = useForm({
    defaultValues: { name: defaultApiKeyName },
    validators: { onChange: tokenNameSchema },
    onSubmit: ({ value }) => {
      mutate(value.name.trim());
    }
  });

  const nameField = useField({ form, name: 'name' });

  const rawError = nameField.state.meta.errors[0];
  let error = '';

  if (typeof rawError === 'string') {
    error = rawError;
  } else if (rawError && typeof rawError === 'object' && 'message' in rawError) {
    error = String(rawError.message);
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    nameField.handleChange(event.target.value);
  };

  const onGenerate = () => {
    void form.handleSubmit();
  };

  const exportToCsv = () => {
    const header = 'Name,Key';
    const token = newApiKey?.prefixedRawApiKey ?? '';
    const csv = [header, `${newApiKey?.label},${token}`].join('\n');
    triggerBlobDownload(new Blob([csv], { type: 'text/csv' }), 'key.csv');
  };

  const handleClose = (setOpen: (state: boolean) => void) => {
    setGenerated(false);
    setNewApiKey(null);
    setOpen(false);
  };

  return {
    handleChange,
    apiKeyName: nameField.state.value,
    onGenerate,
    exportToCsv,
    handleClose,
    generated,
    error,
    loading,
    isError,
    newApiKey
  };
};

export default useGenerationBody;
